<template>
  <div v-if="!thereWasAProblem">
    <div class="w-100 d-flex flex-column flex-sm-row ga-1 ga-sm-3">
      <SiteSelector v-model="queryParams.selectedSiteId" class="flex-1" />
      <div class="flex-1 flex-shrink-0">
        <VueDatePicker
          v-model="queryParams.dateFilter"
          :locale="locale"
          :cancel-text="t('form.cancel')"
          :select-text="t('form.select')"
          format="dd/MM/yyyy"
          :placeholder="t('payouts.list.filters.dateRange')"
          :range="{ noDisabledRange: true }"
          :max-date="new Date()"
          :enable-time-picker="false"
          :preset-dates="datePickerPresetRanges"
        />
      </div>
    </div>

    <div class="d-flex flex-row-reverse justify-space-between mt-4 align-center">
      <v-tooltip location="bottom" :disabled="!reportDisabled">
        <template #activator="{ props }">
          <div v-bind="props">
            <v-btn
              color="secondary"
              :disabled="reportDisabled"
              :loading="reportLoading"
              @click="downloadReport"
            >
              <v-icon>mdi-download</v-icon>
            </v-btn>
          </div>
        </template>
        {{ reportDisabledText }}
      </v-tooltip>
      <h3 v-if="lastWeek" class="text-primary">
        {{ t("payouts.list.lastWeek", { amount: n(lastWeek, "currency") }) }}
      </h3>
    </div>
    <v-alert
      v-if="missingPermissions"
      type="warning"
      variant="outlined"
      density="compact"
      class="my-3"
    >
      {{ t("payouts.list.selectSite") }}
    </v-alert>
    <br />

    <v-skeleton-loader v-if="itemsLoading" class="mx-auto" type="list-item-two-line@5" />
    <div v-if="!itemsLoading && items.length === 0" class="text-center text-gray">
      {{ t("payouts.list.noData") }}
    </div>
    <div v-if="!itemsLoading && items.length > 0 && !missingPermissions">
      <v-expansion-panels v-model="activeItem" focusable>
        <v-expansion-panel v-for="(item, i) in items" :key="i">
          <v-expansion-panel-title>
            <div>
              {{ item.siteName }}<br />
              <span class="text-uppercase text-disabled caption"
                >{{ t("payouts.list.headers.date") }}: {{ item.uxDate }}</span
              >
            </div>
            <div class="ms-auto">
              {{ item.uxAmount }}
              <br />
              <span class="text-uppercase text-disabled caption">{{
                t("payouts.list.headers.amount")
              }}</span>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <DailySummary :days="item.payoutContent" :default-currency="item.currency" />
            <br />
            <v-container>
              <v-row no-gutters>
                <v-col>
                  <v-btn-nd :to="`/payouts/${item.yetiTransferId}`">
                    {{ t("payouts.list.details") }}
                  </v-btn-nd>
                </v-col>
                <v-col class="text-right">
                  <kbd>{{ item.yetiTransferId }}</kbd
                  ><br />
                  <span class="text-uppercase text--disabled caption">{{
                    t("payouts.list.headers.reference")
                  }}</span>
                </v-col>
              </v-row>
            </v-container>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      <br />
      <InfinitePagination v-model:current-page="currentPage" :last-key-pages="lastKeyPages" />
    </div>
  </div>

  <v-alert v-if="thereWasAProblem" color="red lighten-2" dark>
    {{ t("payouts.list.errorMessage", { email: "help@yetipay.me" }) }}
  </v-alert>
  <DownloadDialog v-model="reportDownloadDialog" :report-id="reportId" />
</template>

<script setup lang="ts">
import {
  differenceInYears,
  endOfMonth,
  endOfWeek,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  format,
  isSameDay,
  addDays,
} from "date-fns";
import * as _ from "lodash-es";
import { ref, computed, watch, onMounted, inject } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { Payout } from "@/api/merchant-service/payout";
import { API } from "@/plugins/api";
import { prepareDatesForApi } from "@/store/transactions";

const api = inject<API>("api");
const route = useRoute();
const { t, n, locale } = useI18n();

type EnhancedPayout = Payout & { currency: string; uxAmount: string; uxDate: string };

const datePickerPresetRanges = [
  { label: t("form.datePicker.presets.today"), value: [new Date(), new Date()] },
  {
    label: t("form.datePicker.presets.yesterday"),
    value: [subDays(new Date(), 1), subDays(new Date(), 1)],
  },
  {
    label: t("form.datePicker.presets.thisWeek"),
    value: [startOfWeek(new Date(), { weekStartsOn: 1 }), new Date()],
  },
  {
    label: t("form.datePicker.presets.lastWeek"),
    value: [
      startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
      endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
    ],
  },
  { label: t("form.datePicker.presets.thisMonth"), value: [startOfMonth(new Date()), new Date()] },
  {
    label: t("form.datePicker.presets.lastMonth"),
    value: [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))],
  },
  { label: t("form.datePicker.presets.last90Days"), value: [subDays(new Date(), 90), new Date()] },
  { label: t("form.datePicker.presets.thisYear"), value: [startOfYear(new Date()), new Date()] },
];

const currentPage = ref(1);
const items = ref<EnhancedPayout[]>([]);
const activeItem = ref<Payout | undefined>(undefined);
const itemsLoading = ref(false);
const lastKeyPages = ref<(string | undefined)[]>([undefined]);
const thereWasAProblem = ref(false);
const missingPermissions = ref(false);
const queryParams = ref<{ selectedSiteId?: string; dateFilter: Date[] }>({
  selectedSiteId: undefined,
  dateFilter: [],
});

const dateFilterQueryText = computed(() => {
  if (!queryParams.value.dateFilter) {
    return "";
  }
  let dates = [...queryParams.value.dateFilter]
    .filter((d) => !!d)
    .sort((a, b) => a.getTime() - b.getTime());
  return dates.map((d) => d.toISOString().split("T")[0]).join(",");
});
const internalAllQueryParams = computed(() => {
  return JSON.stringify(queryParams.value);
});

watch(currentPage, async (newPage) => await fetchData(newPage));
watch(internalAllQueryParams, async () => {
  const query = _.cloneDeep(route.query);

  if (queryParams.value.selectedSiteId && queryParams.value.selectedSiteId !== "null") {
    query.siteId = queryParams.value.selectedSiteId;
  } else {
    queryParams.value.selectedSiteId = undefined;
    delete query.siteId;
  }

  if (queryParams.value.dateFilter && queryParams.value.dateFilter.length > 0) {
    query.date = dateFilterQueryText.value;
  } else {
    delete query.date;
  }

  const newQueryParams = Object.keys(query)
    .map((q) => q + "=" + query[q])
    .join("&");
  await fetchData(currentPage.value, true);
  history.pushState({}, "", route.path + (newQueryParams.length > 0 ? "?" + newQueryParams : ""));
});

onMounted(async () => {
  if (route.query.siteId) {
    queryParams.value.selectedSiteId = route.query.siteId as string;
  }
  if (route.query.date) {
    queryParams.value.dateFilter = (route.query.date as string).split(",").map((d) => new Date(d));
  }
  if (!queryParams.value.selectedSiteId && queryParams.value.dateFilter.length === 0) {
    await fetchData(currentPage.value);
  }
});

const fetchData = async (page: number, reset = false) => {
  if (reset) {
    currentPage.value = 1;
    lastKeyPages.value = [undefined];
    activeItem.value = undefined;
  }

  const lastKeyPagesIndex = page - 1;
  if (lastKeyPagesIndex >= lastKeyPages.value.length) {
    return;
  }

  itemsLoading.value = true;
  try {
    const result = await api!.payout.listPayouts(
      queryParams.value.selectedSiteId,
      dateFilterQueryText.value,
      lastKeyPages.value[lastKeyPagesIndex],
    );
    activeItem.value = undefined;
    items.value = result.items.map((item) => {
      const enhancedItem: EnhancedPayout = {
        ...item,
        currency: item.amount.currency,
        uxAmount: item.amount.value.toLocaleString("en-UK", {
          style: "currency",
          currency: item.amount.currency,
        }),
        uxDate: format(new Date(item.date), "yyyy.MM.dd"),
      };
      return enhancedItem;
    });
    lastKeyPages.value[page] = result.lastKey; // this is for the next page, so the index is actually correct
    missingPermissions.value = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error(error);
    if (error.response?.status === 403) {
      missingPermissions.value = true;
    } else {
      thereWasAProblem.value = true;
    }
  } finally {
    itemsLoading.value = false;
  }
};

const reportLoading = ref(false);
const reportDisabled = computed(() => {
  const noResult = items.value.length === 0;
  const noDates = !queryParams.value.dateFilter || queryParams.value.dateFilter?.length != 2;
  const datesTooFarApart =
    queryParams.value.dateFilter?.length === 2 &&
    differenceInYears(queryParams.value.dateFilter[1], queryParams.value.dateFilter[0]) > 0;
  return noResult || reportLoading.value || noDates || datesTooFarApart;
});
const reportDisabledText = computed(() => {
  if (reportLoading.value) {
    return t("payouts.list.download.generating");
  }
  if (itemsLoading.value) {
    return t("payouts.list.download.loading");
  }
  if (items.value.length === 0) {
    return t("payouts.list.download.noEntries");
  }
  if (!queryParams.value.dateFilter || queryParams.value.dateFilter?.length != 2) {
    return t("payouts.list.download.selectDateRange");
  }
  if (
    queryParams.value.dateFilter?.length === 2 &&
    differenceInYears(queryParams.value.dateFilter[1], queryParams.value.dateFilter[0]) > 0
  ) {
    return t("payouts.list.download.dateRangeTooLarge");
  }
  return t("payouts.list.download.generating");
});
const reportId = ref<string | undefined>(undefined);
const reportDownloadDialog = ref(false);
const downloadReport = async () => {
  reportLoading.value = true;
  try {
    const result = await api!.report.createPayoutReport(
      queryParams.value.selectedSiteId,
      prepareDatesForApi(queryParams.value.dateFilter),
    );
    reportId.value = result;
    reportDownloadDialog.value = true;
  } catch (e) {
    console.error(e);
  } finally {
    reportLoading.value = false;
  }
};

// Quick hack to please customer '365'
const lastWeek = ref<number | undefined>(undefined);
watch(
  () => ({ items: items.value }),
  async () => {
    const lastWeekDates = [
      startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
      addDays(endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }), 1),
    ];
    if (
      !queryParams.value.selectedSiteId ||
      queryParams.value.dateFilter.length < 2 ||
      !isSameDay(queryParams.value.dateFilter[0], lastWeekDates[0]) ||
      !isSameDay(queryParams.value.dateFilter[1], lastWeekDates[1])
    ) {
      lastWeek.value = undefined;
      return;
    }
    lastWeek.value =
      items.value
        .map((item) => item.amount.value)
        .slice(0, 7)
        .reduce((acc, item) => acc + item, 0) ?? 0;
  },
  { deep: true },
);
</script>
